import React, { useEffect, useState } from 'react';
import { HashRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { LanguageProvider, useLang } from '@shootout-comps/i18n';
import { AuthProvider, useAuth, TournamentProvider, LeagueProvider, completeTournamentCreation } from '@shootout-comps/core';
import { useTournament } from '@shootout-comps/core';
import type { TournamentSettingsInput } from '@shootout-comps/core';
import webStorage from './adapters/webStorage';
import { useSocket, setAccessTokenGetter, useTournamentSocket, useConnectionStatus } from './adapters/webSocket';
import webOAuthLauncher from './adapters/webOAuthLauncher';
import { useRequiredParams } from './hooks/useRequiredParams';
import AppLoginPage from './pages/AppLoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import Header from './components/Header';
import PlayersPage from './pages/PlayersPage';
import PlayersAdminPage from './pages/PlayersAdminPage';
import BracketPage from './pages/BracketPage';
import MatchPlayPage from './pages/MatchPlayPage';
import TournamentsPage from './pages/TournamentsPage';
import HistoricTournamentPage from './pages/HistoricTournamentPage';
import UsersPage from './pages/UsersPage';
import SuperuserSettingsPage from './pages/SuperuserSettingsPage';
import AdminStatsPage from './pages/AdminStatsPage';
// AdminPage removed — tournament management now lives in the Tournaments dialog
import UserProfilePage from './pages/UserProfilePage';
import EmailVerificationPage from './pages/EmailVerificationPage';
import SignupPage from './pages/SignupPage';
import SplashPage from './pages/SplashPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';

import ContactPage from './pages/ContactPage';
import FaqPage from './pages/FaqPage';
import PublicTournamentsPage from './pages/PublicTournamentsPage';
import PublicTournamentBracketPage from './pages/PublicTournamentBracketPage';
import SpectateMatchPage from './pages/SpectateMatchPage';
import LeaguesLandingPage from './pages/leagues/LeaguesLandingPage';

type AuthView = 'splash' | 'login' | 'signup' | 'verify-pending' | 'verify-success' | 'verify-error' | 'forgot-password' | 'privacy';

function getUrlParams() {
  const p = new URLSearchParams(window.location.search);
  return {
    verify:      p.get('verify') as 'success'|'error'|null,
    reason:      p.get('reason') ?? '',
    username:    p.get('username') ?? '',
    reset:       p.get('reset') as 'set'|'error'|null,
    token:       p.get('token') ?? '',
    payment:     p.get('payment') as 'success'|'cancelled'|null,
    sessionId:   p.get('session_id') ?? '',
    emailChange: p.get('email_change') as 'success'|'error'|null,
    oauth:       p.get('oauth') as 'success'|'error'|null,
    code:        p.get('code') ?? '',
    leagueLink:  p.get('leagueLink') as 'confirmed'|'rejected'|'error'|null,
  };
}

// ─── Route adapters ─────────────────────────────────────────────────────────
// Thin translation layer between the URL and each page's existing, unchanged prop contract
// (tournamentId, onBack, onSelectMatch, ...) — only these adapters know about the router.

function TournamentsRouteAdapter() {
  const navigate = useNavigate();
  return (
    <TournamentsPage
      onOpenTournament={(id, status) => navigate(status === 'registration' ? `/tournament/${id}/players` : `/tournament/${id}/bracket`)}
      onViewHistoric={id => navigate(`/tournaments/${id}`)}
    />
  );
}

function HistoricTournamentRouteAdapter() {
  const params = useRequiredParams<{ tournamentId: string }>(['tournamentId'], '/tournaments');
  const navigate = useNavigate();
  if (!params) return null;
  return <HistoricTournamentPage tournamentId={params.tournamentId} onBack={() => navigate('/tournaments')} />;
}

function PlayersRouteAdapter() {
  const params = useRequiredParams<{ tournamentId: string }>(['tournamentId'], '/tournaments');
  if (!params) return null;
  return <PlayersPage tournamentId={params.tournamentId} />;
}

function BracketRouteAdapter() {
  const params = useRequiredParams<{ tournamentId: string }>(['tournamentId'], '/tournaments');
  const navigate = useNavigate();
  if (!params) return null;
  return <BracketPage tournamentId={params.tournamentId} onSelectMatch={id => navigate(`/tournament/${params.tournamentId}/bracket/match/${id}`)} />;
}

function MatchPlayRouteAdapter() {
  const params = useRequiredParams<{ tournamentId: string; matchId: string }>(['tournamentId', 'matchId'], '/tournaments');
  const navigate = useNavigate();
  const { fetchTournament } = useTournament();
  if (!params) return null;
  return (
    <MatchPlayPage
      matchId={params.matchId}
      onBack={() => { fetchTournament(params.tournamentId); navigate(`/tournament/${params.tournamentId}/bracket`); }}
    />
  );
}

function PlayersAdminRouteAdapter() {
  const navigate = useNavigate();
  return <PlayersAdminPage onNavigateToTournament={id => navigate(`/tournaments/${id}`)} />;
}

function PublicTournamentsRouteAdapter() {
  const navigate = useNavigate();
  return <PublicTournamentsPage onSelectTournament={id => navigate(`/public/${id}`)} />;
}

function PublicBracketRouteAdapter() {
  const params = useRequiredParams<{ tournamentId: string }>(['tournamentId'], '/public');
  const navigate = useNavigate();
  if (!params) return null;
  return (
    <PublicTournamentBracketPage
      tournamentId={params.tournamentId}
      onBack={() => navigate('/public')}
      onSpectateMatch={matchId => navigate(`/public/${params.tournamentId}/spectate/${matchId}`)}
    />
  );
}

function SpectateRouteAdapter() {
  const params = useRequiredParams<{ tournamentId: string; matchId: string }>(['tournamentId', 'matchId'], '/public');
  const navigate = useNavigate();
  if (!params) return null;
  return <SpectateMatchPage tournamentId={params.tournamentId} matchId={params.matchId} onBack={() => navigate(`/public/${params.tournamentId}`)} />;
}

function FaqRouteAdapter() {
  const navigate = useNavigate();
  return <FaqPage onNavigateToContact={() => navigate('/contact')} />;
}

function ProfileRouteAdapter() {
  const navigate = useNavigate();
  return <UserProfilePage onBack={() => navigate(-1)} onPrivacy={() => navigate('/privacy')} />;
}

function PrivacyRouteAdapter() {
  const navigate = useNavigate();
  return <PrivacyPolicyPage onBack={() => navigate(-1)} />;
}

// ─── Authenticated app shell ─────────────────────────────────────────────────
function AppInner() {
  const { confirmPayment, createTournament, updateName, updateSettings } = useTournament();
  const navigate = useNavigate();

  // Platform-wide feature flag — no shared settings hook exists in this codebase (every page
  // independently fetches /api/settings/public), so this follows the same one-off convention.
  const [leaguesEnabled, setLeaguesEnabled] = useState(false);
  useEffect(() => {
    fetch('/api/settings/public')
      .then(r => r.json())
      .then((d: { leaguesEnabled?: boolean }) => setLeaguesEnabled(!!d.leaguesEnabled))
      .catch(() => setLeaguesEnabled(false));
  }, []);

  const [stripeReturnError, setStripeReturnError] = useState('');

  // ── Handle Stripe Checkout return (success or cancelled) ─────────────────
  useEffect(() => {
    const urlp = getUrlParams();
    if (!urlp.payment) return;

    if (urlp.payment === 'cancelled') {
      navigate('/', { replace: true });
      return;
    }

    if (urlp.payment === 'success' && urlp.sessionId) {
      navigate('/', { replace: true });
      // Retrieve the pending tournament data saved before the redirect
      const pendingRaw = sessionStorage.getItem('pendingTournament');
      sessionStorage.removeItem('pendingTournament');
      if (!pendingRaw) return;

      const pending = JSON.parse(pendingRaw) as {
        name: string;
        settings: TournamentSettingsInput;
      };

      (async () => {
        try {
          const { paymentToken } = await confirmPayment(urlp.sessionId);
          const created = await completeTournamentCreation({
            createTournament, updateName, updateSettings,
            paymentToken, name: pending.name, settings: pending.settings,
          });
          navigate(`/tournament/${created._id}/players`);
        } catch (e) {
          setStripeReturnError(e instanceof Error ? e.message : 'Payment failed after return from Stripe');
        }
      })();
    }
  }, []); // eslint-disable-line

  const errorBanner = stripeReturnError ? (
    <div style={{ background: 'rgba(192,57,43,0.15)', border: '1px solid rgba(192,57,43,0.4)', borderRadius: '8px', padding: '0.75rem 1rem', color: '#e74c3c', fontSize: '0.85rem', margin: '1rem var(--page-pad) 0' }}>
      ⚠ Payment error: {stripeReturnError}
      <button onClick={() => setStripeReturnError('')} style={{ marginLeft: '1rem', background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: '0.85rem' }}>✕</button>
    </div>
  ) : null;

  return (
    <div>
      <Header leaguesEnabled={leaguesEnabled} />
      <main style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        {errorBanner}
        <Routes>
          <Route index element={<Navigate to="/tournaments" replace />} />
          <Route path="tournaments" element={<TournamentsRouteAdapter />} />
          <Route path="tournaments/:tournamentId" element={<HistoricTournamentRouteAdapter />} />
          <Route path="tournaments/:tournamentId/match/:matchId" element={<HistoricTournamentRouteAdapter />} />
          <Route path="tournament/:tournamentId/players" element={<PlayersRouteAdapter />} />
          <Route path="tournament/:tournamentId/bracket" element={<BracketRouteAdapter />} />
          <Route path="tournament/:tournamentId/bracket/match/:matchId" element={<MatchPlayRouteAdapter />} />
          <Route path="players" element={<PlayersAdminRouteAdapter />} />
          <Route path="public" element={<PublicTournamentsRouteAdapter />} />
          <Route path="public/:tournamentId" element={<PublicBracketRouteAdapter />} />
          <Route path="public/:tournamentId/spectate/:matchId" element={<SpectateRouteAdapter />} />
          {leaguesEnabled && <Route path="leagues/*" element={<LeaguesLandingPage />} />}
          <Route path="admin/users/*" element={<UsersPage />} />
          <Route path="admin/settings" element={<SuperuserSettingsPage />} />
          <Route path="admin/stats" element={<AdminStatsPage />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="faq" element={<FaqRouteAdapter />} />
          <Route path="profile" element={<ProfileRouteAdapter />} />
          <Route path="privacy" element={<PrivacyRouteAdapter />} />
          <Route path="*" element={<Navigate to="/tournaments" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function SnookerSpinner() {
  // Inline spinner using CSS — avoids circular import
  return (
    <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'radial-gradient(circle at 35% 35%, #ff6b6b, #c0392b)', display: 'inline-block', animation: 'spin 1.5s linear infinite' }}>
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

// ─── Auth gate ───────────────────────────────────────────────────────────────
// The pre-auth screens that get real, bookmarkable/back-button-able URLs. Everything else
// in AuthView (verify-pending/success/error) stays local state, reached without moving
// the URL — see the render-order comment further down for why that's safe.
const ROUTED_AUTH_PATHS: Record<string, 'login' | 'signup' | 'forgot-password' | 'privacy'> = {
  '/login': 'login', '/signup': 'signup', '/forgot-password': 'forgot-password', '/privacy': 'privacy',
};

function AuthGate() {
  const { isAuthenticated, isLoading, exchangeOAuthCode, getAccessToken } = useAuth();
  const { t } = useLang();
  // Registered before the socket connects (see createSocketSingleton) so the handshake can
  // always send a fresh token — reconnection attempts pick up getAccessToken's latest value.
  useEffect(() => { setAccessTokenGetter(getAccessToken); }, [getAccessToken]);
  useSocket();
  const urlp = getUrlParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [view, setView]                 = useState<AuthView>(() => {
    if (urlp.verify === 'success') return 'verify-success';
    if (urlp.verify === 'error')   return 'verify-error';
    return 'splash';
  });
  const [pendingEmail, setPendingEmail] = useState('');

  // Derived purely from the URL — self-heals on browser back/forward with no separate sync
  // effect needed. Only consulted for the login/signup/forgot-password/privacy/splash branches
  // below; verify-pending/verify-success/verify-error are checked earlier using raw `view` and
  // never reach this (see render order below), so this only ever needs to resolve those five.
  const effectiveAuthView: 'splash' | 'login' | 'signup' | 'forgot-password' | 'privacy' =
    ROUTED_AUTH_PATHS[location.pathname] ?? 'splash';

  const navigateTo = (v: AuthView) => {
    setView(v);
    if (v === 'login') navigate('/login');
    else if (v === 'signup') navigate('/signup');
    else if (v === 'forgot-password') navigate('/forgot-password');
    else if (v === 'privacy') navigate('/privacy');
    else if (v === 'splash') navigate('/');
  };
  const [accountSuspended, setAccountSuspended] = useState<{ reason: string } | null>(null);
  const [oauthError, setOauthError]           = useState('');
  const [oauthExchanging, setOauthExchanging] = useState(urlp.oauth === 'success' && !!urlp.code);

  const clearUrl = () => navigate('/', { replace: true });

  const handleLoginError = (err: Error & { reason?: string; email?: string }) => {
    if (err.message === 'PENDING_VERIFICATION') {
      setPendingEmail(err.email ?? ''); setView('verify-pending');
    } else if (err.message === 'ACCOUNT_SUSPENDED') {
      setAccountSuspended({ reason: err.reason ?? '' });
    } else {
      throw err;
    }
  };

  // ── OAuth (Google/Facebook) redirect return ───────────────────────────────
  useEffect(() => {
    if (urlp.oauth === 'success' && urlp.code) {
      exchangeOAuthCode(urlp.code)
        .then(() => { clearUrl(); })
        .catch(() => { setOauthError('generic'); clearUrl(); })
        .finally(() => setOauthExchanging(false));
    }
  }, []); // eslint-disable-line

  // ── Password reset flow — shown to everyone regardless of auth ───────────
  if (urlp.reset === 'set' && urlp.token) {
    return (
      <ResetPasswordPage
        token={urlp.token}
        onDone={() => { clearUrl(); }}
      />
    );
  }
  if (urlp.reset === 'error') {
    return (
      <div style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--baize-dark)', padding: '1rem' }}>
        <div style={{ maxWidth: '400px', width: '100%', textAlign: 'center', background: 'rgba(192,57,43,0.12)', border: '2px solid rgba(192,57,43,0.4)', borderRadius: '14px', padding: '2.5rem' }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>⚠️</div>
          <h2 style={{ fontFamily: 'Bebas Neue', fontSize: '1.8rem', color: '#e74c3c', marginBottom: '0.5rem' }}>{t.resetLinkInvalidTitle}</h2>
          <p style={{ color: 'var(--chalk-dim)', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
            {urlp.reason === 'expired' ? t.resetLinkExpiredMsg : t.resetLinkInvalidMsg}
          </p>
          <button onClick={() => { clearUrl(); }} style={{ background: 'transparent', color: 'var(--chalk-dim)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '0.65rem 1.5rem', cursor: 'pointer', minHeight: '44px' }}>
            Back to App
          </button>
        </div>
      </div>
    );
  }

  // ── Verify flows — shown to everyone ─────────────────────────────────────
  if (urlp.verify === 'success' || view === 'verify-success') return (
    <EmailVerificationPage verifySuccess username={urlp.username} onVerified={() => { clearUrl(); navigateTo('login'); }} onSignIn={() => { clearUrl(); navigateTo('login'); }} />
  );
  if (urlp.verify === 'error' || view === 'verify-error') return (
    <EmailVerificationPage verifyError={urlp.reason || 'invalid_token'} email={pendingEmail || undefined} onVerified={() => { clearUrl(); navigateTo('login'); }} onSignIn={() => { clearUrl(); navigateTo('login'); }} />
  );

  // ── Email change confirmation ─────────────────────────────────────────────
  if (urlp.emailChange === 'success') return (
    <div style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--baize-dark)', padding: '1rem' }}>
      <div style={{ maxWidth: '400px', width: '100%', textAlign: 'center', background: 'rgba(46,204,113,0.08)', border: '2px solid rgba(46,204,113,0.3)', borderRadius: '14px', padding: '2.5rem' }}>
        <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>✅</div>
        <h2 style={{ fontFamily: 'Bebas Neue', fontSize: '1.8rem', color: '#2ecc71', marginBottom: '0.5rem' }}>{t.emailUpdatedTitle}</h2>
        <p style={{ color: 'var(--chalk-dim)', fontSize: '0.85rem', marginBottom: '1.5rem' }}>Your email address has been changed successfully. Please sign in with your new address.</p>
        <button onClick={() => { clearUrl(); }} style={{ background: 'linear-gradient(135deg,var(--gold),var(--gold-light))', color: 'var(--baize-dark)', border: 'none', borderRadius: '8px', padding: '0.65rem 1.5rem', cursor: 'pointer', fontWeight: 700, fontSize: '0.9rem', minHeight: '44px' }}>
          Continue
        </button>
      </div>
    </div>
  );
  if (urlp.emailChange === 'error') return (
    <div style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--baize-dark)', padding: '1rem' }}>
      <div style={{ maxWidth: '400px', width: '100%', textAlign: 'center', background: 'rgba(192,57,43,0.12)', border: '2px solid rgba(192,57,43,0.4)', borderRadius: '14px', padding: '2.5rem' }}>
        <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>⚠️</div>
        <h2 style={{ fontFamily: 'Bebas Neue', fontSize: '1.8rem', color: '#e74c3c', marginBottom: '0.5rem' }}>{t.emailChangeLinkInvalidTitle}</h2>
        <p style={{ color: 'var(--chalk-dim)', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
          {urlp.reason === 'expired' ? 'This email change link has expired. Please request a new one from your profile.' :
           urlp.reason === 'taken'   ? t.emailAlreadyTaken :
           t.emailChangeLinkInvalidMsg}
        </p>
        <button onClick={() => { clearUrl(); }} style={{ background: 'transparent', color: 'var(--chalk-dim)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '0.65rem 1.5rem', cursor: 'pointer', minHeight: '44px' }}>
          Back to App
        </button>
      </div>
    </div>
  );

  // ── League roster link confirmation (from the email sent when an owner links you) ─────────
  if (urlp.leagueLink === 'confirmed') return (
    <div style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--baize-dark)', padding: '1rem' }}>
      <div style={{ maxWidth: '400px', width: '100%', textAlign: 'center', background: 'rgba(46,204,113,0.08)', border: '2px solid rgba(46,204,113,0.3)', borderRadius: '14px', padding: '2.5rem' }}>
        <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>✅</div>
        <h2 style={{ fontFamily: 'Bebas Neue', fontSize: '1.8rem', color: '#2ecc71', marginBottom: '0.5rem' }}>You're linked!</h2>
        <p style={{ color: 'var(--chalk-dim)', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
          Your account is now linked to that league player. Sign in to see your stats and everything about the league.
        </p>
        <button onClick={() => { clearUrl(); navigateTo('login'); }} style={{ background: 'linear-gradient(135deg,var(--gold),var(--gold-light))', color: 'var(--baize-dark)', border: 'none', borderRadius: '8px', padding: '0.65rem 1.5rem', cursor: 'pointer', fontWeight: 700, fontSize: '0.9rem', minHeight: '44px' }}>
          Sign In
        </button>
      </div>
    </div>
  );
  if (urlp.leagueLink === 'rejected') return (
    <div style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--baize-dark)', padding: '1rem' }}>
      <div style={{ maxWidth: '400px', width: '100%', textAlign: 'center', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '14px', padding: '2.5rem' }}>
        <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>👍</div>
        <h2 style={{ fontFamily: 'Bebas Neue', fontSize: '1.8rem', color: 'var(--chalk)', marginBottom: '0.5rem' }}>Thanks — that's been removed</h2>
        <p style={{ color: 'var(--chalk-dim)', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
          We've let the league organiser know it wasn't you. No further action needed.
        </p>
        <button onClick={() => { clearUrl(); }} style={{ background: 'transparent', color: 'var(--chalk-dim)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '0.65rem 1.5rem', cursor: 'pointer', minHeight: '44px' }}>
          Back to App
        </button>
      </div>
    </div>
  );
  if (urlp.leagueLink === 'error') return (
    <div style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--baize-dark)', padding: '1rem' }}>
      <div style={{ maxWidth: '400px', width: '100%', textAlign: 'center', background: 'rgba(192,57,43,0.12)', border: '2px solid rgba(192,57,43,0.4)', borderRadius: '14px', padding: '2.5rem' }}>
        <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>⚠️</div>
        <h2 style={{ fontFamily: 'Bebas Neue', fontSize: '1.8rem', color: '#e74c3c', marginBottom: '0.5rem' }}>Link not valid</h2>
        <p style={{ color: 'var(--chalk-dim)', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
          {urlp.reason === 'expired' ? 'This link has expired. Ask the league organiser to send it again.' : 'This link is invalid or has already been used.'}
        </p>
        <button onClick={() => { clearUrl(); }} style={{ background: 'transparent', color: 'var(--chalk-dim)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '0.65rem 1.5rem', cursor: 'pointer', minHeight: '44px' }}>
          Back to App
        </button>
      </div>
    </div>
  );

  // ── OAuth (Google/Facebook) redirect return ───────────────────────────────
  if (oauthExchanging) return (
    <div style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--baize-dark)' }}>
      <SnookerSpinner />
    </div>
  );
  if (urlp.oauth === 'error' || oauthError) return (
    <div style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--baize-dark)', padding: '1rem' }}>
      <div style={{ maxWidth: '400px', width: '100%', textAlign: 'center', background: 'rgba(192,57,43,0.12)', border: '2px solid rgba(192,57,43,0.4)', borderRadius: '14px', padding: '2.5rem' }}>
        <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>⚠️</div>
        <h2 style={{ fontFamily: 'Bebas Neue', fontSize: '1.8rem', color: '#e74c3c', marginBottom: '0.5rem' }}>{t.oauthErrorTitle}</h2>
        <p style={{ color: 'var(--chalk-dim)', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
          {urlp.reason === 'provider_denied'     ? t.oauthErrorDenied :
           urlp.reason === 'email_not_verified'  ? t.oauthErrorEmailNotVerified :
           urlp.reason === 'account_suspended'   ? t.oauthErrorAccountSuspended :
           urlp.reason === 'provider_disabled'   ? t.oauthErrorProviderDisabled :
           t.oauthErrorGeneric}
        </p>
        <button onClick={() => { clearUrl(); setOauthError(''); }} style={{ background: 'transparent', color: 'var(--chalk-dim)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '0.65rem 1.5rem', cursor: 'pointer', minHeight: '44px' }}>
          Back to App
        </button>
      </div>
    </div>
  );

  // ── Loading spinner ───────────────────────────────────────────────────────
  if (isLoading) return (
    <div style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--baize-dark)' }}>
      <SnookerSpinner />
    </div>
  );

  // ── Authenticated ─────────────────────────────────────────────────────────
  if (isAuthenticated) {
    return (
      <TournamentProvider apiBaseUrl="" useTournamentSocket={useTournamentSocket} useConnectionStatus={useConnectionStatus}>
        <LeagueProvider apiBaseUrl="">
          <AppInner />
        </LeagueProvider>
      </TournamentProvider>
    );
  }

  // ── Account suspended ─────────────────────────────────────────────────────
  if (accountSuspended) return (
    <div style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--baize-dark)', padding: '1rem' }}>
      <div style={{ maxWidth: '420px', width: '100%', background: 'rgba(192,57,43,0.12)', border: '2px solid rgba(192,57,43,0.4)', borderRadius: '14px', padding: '2.5rem', textAlign: 'center' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>🚫</div>
        <h2 style={{ fontFamily: 'Bebas Neue', fontSize: '1.8rem', color: '#e74c3c', marginBottom: '0.5rem' }}>{t.accountSuspendedTitle}</h2>
        <p style={{ color: 'var(--chalk-dim)', marginBottom: '0.75rem', fontSize: '0.88rem' }}>{t.accountSuspendedMsg}</p>
        {accountSuspended.reason && <p style={{ color: '#e74c3c', background: 'rgba(192,57,43,0.15)', borderRadius: '6px', padding: '0.6rem', fontSize: '0.85rem', marginBottom: '1.25rem' }}>{accountSuspended.reason}</p>}
        <button onClick={() => setAccountSuspended(null)} style={{ background: 'transparent', color: 'var(--chalk-dim)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '0.65rem 1.5rem', cursor: 'pointer', minHeight: '44px' }}>Back</button>
      </div>
    </div>
  );

  // ── Unauthenticated views ─────────────────────────────────────────────────
  if (view === 'verify-pending') return (
    <EmailVerificationPage email={pendingEmail} onVerified={() => navigateTo('login')} onSignIn={() => navigateTo('login')} />
  );
  if (effectiveAuthView === 'privacy') return (
    <PrivacyPolicyPage onBack={() => navigate(-1)} />
  );
  if (effectiveAuthView === 'signup') return (
    <SignupPage onSignIn={() => navigateTo('login')} onSignedUp={email => { setPendingEmail(email); navigateTo('verify-pending'); }} onPrivacy={() => navigateTo('privacy')} />
  );
  if (effectiveAuthView === 'login') return (
    <AppLoginPage onSignUp={() => navigateTo('signup')} onLoginError={handleLoginError} onForgotPassword={() => navigateTo('forgot-password')} />
  );
  if (effectiveAuthView === 'forgot-password') return (
    <ForgotPasswordPage onBack={() => navigateTo('login')} onDone={() => navigateTo('login')} />
  );
  return <SplashPage onSignIn={() => navigateTo('login')} onSignUp={() => navigateTo('signup')} onPrivacy={() => navigateTo('privacy')} />;
}

export default function App() {
  return (
    <LanguageProvider
      storage={webStorage}
      detectLang={() => navigator.language}
      onLangChange={(lang, dir) => {
        document.documentElement.dir = dir;
        document.documentElement.lang = lang;
      }}
    >
      <HashRouter>
        <AuthProvider storage={webStorage} apiBaseUrl="" oauthTarget="web" oauthLauncher={webOAuthLauncher}>
          <AuthGate />
        </AuthProvider>
      </HashRouter>
    </LanguageProvider>
  );
}
